import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, Shield, Users, FileText, Calendar, AlertTriangle, 
  CheckCircle, Clock, Download, Upload, Mail, Phone, Building,
  DollarSign, Eye, Edit, Plus, Search, Filter
} from "lucide-react";
import { useLocation } from "wouter";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import { useQuery } from "@tanstack/react-query";

export default function COBRADetails() {
  const [, setLocation] = useLocation();
  const [selectedTab, setSelectedTab] = useState("overview");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const { data: employees = [] } = useQuery<any[]>({
    queryKey: ["/api/employees"],
  });

  // Mock COBRA beneficiaries data
  const [cobraBeneficiaries] = useState([
    {
      id: "1",
      employeeId: 1,
      employeeName: "Sarah Johnson",
      terminationDate: "2024-05-15",
      notificationSentDate: "2024-05-16",
      electionDeadline: "2024-07-14",
      status: "pending-election",
      dependents: ["Spouse: Michael Johnson", "Child: Emma Johnson (Age 12)"],
      planType: "Health + Dental",
      monthlyPremium: 850,
      lastContact: "2024-05-20",
      address: "123 Main St, Austin, TX 78701",
      phone: "(555) 123-4567",
      email: "sarah.johnson@email.com",
      reason: "Voluntary Termination"
    },
    {
      id: "2",
      employeeId: 2,
      employeeName: "David Chen",
      terminationDate: "2024-04-10",
      notificationSentDate: "2024-04-11",
      electionDeadline: "2024-06-09",
      status: "elected",
      dependents: ["Spouse: Lisa Chen"],
      planType: "Health Only",
      monthlyPremium: 520,
      lastContact: "2024-04-15",
      address: "456 Oak Ave, Dallas, TX 75201",
      phone: "(555) 987-6543",
      email: "david.chen@email.com",
      reason: "Reduction in Hours",
      electionDate: "2024-04-25",
      coverageStartDate: "2024-05-01"
    },
    {
      id: "3",
      employeeId: 3,
      employeeName: "Maria Rodriguez",
      terminationDate: "2024-03-20",
      notificationSentDate: "2024-03-21",
      electionDeadline: "2024-05-19",
      status: "declined",
      dependents: [],
      planType: "Health + Vision",
      monthlyPremium: 420,
      lastContact: "2024-03-25",
      address: "789 Pine St, Houston, TX 77001",
      phone: "(555) 456-7890",
      email: "maria.rodriguez@email.com",
      reason: "Involuntary Termination",
      declineDate: "2024-03-28"
    }
  ]);

  // Filter beneficiaries
  const filteredBeneficiaries = cobraBeneficiaries.filter(beneficiary => {
    const matchesSearch = !searchTerm || 
      beneficiary.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beneficiary.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || beneficiary.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Calculate metrics
  const totalBeneficiaries = cobraBeneficiaries.length;
  const pendingElections = cobraBeneficiaries.filter(b => b.status === 'pending-election').length;
  const activeCoverage = cobraBeneficiaries.filter(b => b.status === 'elected').length;
  const declined = cobraBeneficiaries.filter(b => b.status === 'declined').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending-election':
        return <Badge variant="secondary">Pending Election</Badge>;
      case 'elected':
        return <Badge variant="default">Coverage Active</Badge>;
      case 'declined':
        return <Badge variant="outline">Declined</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDaysRemaining = (deadline: string) => {
    const days = differenceInDays(parseISO(deadline), new Date());
    return days > 0 ? days : 0;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Client
          </Button>
          <h1 className="text-3xl font-bold">COBRA Management</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Reports
          </Button>
          <Button size="sm">
            <Upload className="h-4 w-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Beneficiaries</CardTitle>
            <Users className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{totalBeneficiaries}</div>
            <p className="text-xs text-muted-foreground">
              All COBRA eligible individuals
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Elections</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingElections}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting election decision
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Coverage</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{activeCoverage}</div>
            <p className="text-xs text-muted-foreground">
              Currently enrolled in COBRA
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-gray-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Declined Coverage</CardTitle>
            <AlertTriangle className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-600">{declined}</div>
            <p className="text-xs text-muted-foreground">
              Waived continuation rights
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="beneficiaries">Beneficiaries</TabsTrigger>
          <TabsTrigger value="notices">Notices & Forms</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Tracking</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  COBRA Compliance Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Initial Notices</span>
                  <Badge variant="default">Compliant</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Election Notices</span>
                  <Badge variant="default">Current</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Premium Collections</span>
                  <Badge variant="default">Up to Date</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Termination Notices</span>
                  <Badge variant="secondary">Review Needed</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Important Deadlines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Sarah Johnson - Election</p>
                      <p className="text-sm text-muted-foreground">Deadline: July 14, 2024</p>
                    </div>
                    <Badge variant="destructive">{getDaysRemaining("2024-07-14")} days</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">David Chen - Premium Due</p>
                      <p className="text-sm text-muted-foreground">Due: June 1, 2024</p>
                    </div>
                    <Badge variant="secondary">5 days</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="font-medium">David Chen elected COBRA coverage</p>
                    <p className="text-sm text-muted-foreground">April 25, 2024 - Health Only plan selected</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="font-medium">Election notice sent to Sarah Johnson</p>
                    <p className="text-sm text-muted-foreground">May 16, 2024 - Via certified mail and email</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="font-medium">Maria Rodriguez declined coverage</p>
                    <p className="text-sm text-muted-foreground">March 28, 2024 - Waiver form received</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Beneficiaries Tab */}
        <TabsContent value="beneficiaries" className="space-y-6">
          {/* Search and Filter */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filter Beneficiaries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search by name or email..." 
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending-election">Pending Election</SelectItem>
                      <SelectItem value="elected">Coverage Active</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      setSearchTerm('');
                      setFilterStatus('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Beneficiaries List */}
          <Card>
            <CardHeader>
              <CardTitle>COBRA Beneficiaries ({filteredBeneficiaries.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {filteredBeneficiaries.map((beneficiary) => (
                  <div key={beneficiary.id} className="border rounded-lg p-4 hover:bg-muted/25 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{beneficiary.employeeName}</h3>
                          {getStatusBadge(beneficiary.status)}
                          {beneficiary.status === 'pending-election' && (
                            <Badge variant="destructive">
                              {getDaysRemaining(beneficiary.electionDeadline)} days left
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Terminated: {format(parseISO(beneficiary.terminationDate), 'MMM d, yyyy')} • {beneficiary.reason}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                        <Button variant="outline" size="sm">
                          <Mail className="h-4 w-4 mr-2" />
                          Send Notice
                        </Button>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{beneficiary.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span>{beneficiary.phone}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>${beneficiary.monthlyPremium}/month</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>Deadline: {format(parseISO(beneficiary.electionDeadline), 'MMM d')}</span>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className="font-medium text-muted-foreground text-sm">Plan: </span>
                      <span className="text-sm">{beneficiary.planType}</span>
                      {beneficiary.dependents.length > 0 && (
                        <>
                          <span className="font-medium text-muted-foreground text-sm ml-4">Dependents: </span>
                          <span className="text-sm">{beneficiary.dependents.join(', ')}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                {filteredBeneficiaries.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="mb-2">No COBRA beneficiaries match your current filters.</p>
                    <p className="text-sm">Try adjusting your search criteria.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notices & Forms Tab */}
        <TabsContent value="notices" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Required COBRA Notices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-blue-500" />
                      <div>
                        <p className="font-medium">Initial COBRA Notice</p>
                        <p className="text-xs text-muted-foreground">General notice of COBRA rights</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-green-500" />
                      <div>
                        <p className="font-medium">Election Notice</p>
                        <p className="text-xs text-muted-foreground">Sent after qualifying event</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-orange-500" />
                      <div>
                        <p className="font-medium">Termination Notice</p>
                        <p className="text-xs text-muted-foreground">End of COBRA coverage</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>COBRA Forms & Documents</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-purple-500" />
                      <div>
                        <p className="font-medium">COBRA Election Form</p>
                        <p className="text-xs text-muted-foreground">Beneficiary election document</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-red-500" />
                      <div>
                        <p className="font-medium">Premium Payment Coupon</p>
                        <p className="text-xs text-muted-foreground">Monthly payment voucher</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-cyan-500" />
                      <div>
                        <p className="font-medium">Coverage Summary</p>
                        <p className="text-xs text-muted-foreground">Plan details and rates</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Notice Tracking</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">12</div>
                    <p className="text-sm text-muted-foreground">Initial Notices Sent</p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-green-600">8</div>
                    <p className="text-sm text-muted-foreground">Election Notices Sent</p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">3</div>
                    <p className="text-sm text-muted-foreground">Termination Notices</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tracking Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                COBRA Compliance Scorecard
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600 mb-2">94%</div>
                <p className="text-muted-foreground">Overall Compliance Score</p>
                <Progress value={94} className="mt-4" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-4">
                  <h4 className="font-semibold">Compliant Areas</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <span className="text-sm">Initial Notice Distribution</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <span className="text-sm">Timely Election Notices</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <span className="text-sm">Premium Collection Process</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">Areas for Review</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                      <span className="text-sm">Termination Notice Timing</span>
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                    </div>
                    <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                      <span className="text-sm">Documentation Storage</span>
                      <Clock className="h-4 w-4 text-yellow-500" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <h4 className="font-semibold mb-3">Upcoming Deadlines</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">Sarah Johnson Election</p>
                          <p className="text-sm text-muted-foreground">60-day election period ends</p>
                        </div>
                        <Badge variant="destructive">7 days</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium">Quarterly Compliance Review</p>
                          <p className="text-sm text-muted-foreground">Q2 2024 assessment due</p>
                        </div>
                        <Badge variant="secondary">15 days</Badge>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Recent Completions</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-950/20">
                        <div>
                          <p className="font-medium">David Chen Premium Payment</p>
                          <p className="text-sm text-muted-foreground">May 2024 payment received</p>
                        </div>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50 dark:bg-green-950/20">
                        <div>
                          <p className="font-medium">Maria Rodriguez Waiver</p>
                          <p className="text-sm text-muted-foreground">Coverage decline documented</p>
                        </div>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}